import {testIdProps} from '@github-ui/test-id-props'
import {Label} from '@primer/react'
import {clsx} from 'clsx'
import {memo, useRef} from 'react'

import useIsVisible from '../../../components/board/hooks/use-is-visible'
import {PILL_PADDING, PILL_STICKY_MARGIN} from '../../../components/roadmap/constants'
import {formatISODateString} from '../../../helpers/parsing'
import {useRoadmapTableWidth} from '../../../hooks/use-roadmap-settings'
import {useRoadmapGetViewport} from '../roadmap-view-provider'
import {getTooltipText} from '../tooltip-helper'
import styles from './roadmap-group-pill.module.css'
import {NavigationArrow} from './roadmap-pill-buttons'
import {RoadmapCell} from './roadmap-table-layout'

type RoadmapGroupPillProps = {
  startDate?: Date
  endDate?: Date
  pillOffsetLeft: number
  pillWidth: number
}

/**
 * Roadmap Group Pill renders with the position and size determined by the min/max of the dates of the items in the group.
 */
export const RoadmapGroupPill = memo(function RoadmapGroupPillComponent({
  startDate,
  endDate,
  pillOffsetLeft,
  pillWidth,
}: RoadmapGroupPillProps) {
  const getViewport = useRoadmapGetViewport()
  const tableWidth = useRoadmapTableWidth()
  const ref = useRef<HTMLDivElement | null>(null)
  const {isVisible} = useIsVisible({ref})

  const leadingEdgeRef = useRef<HTMLDivElement | null>(null)
  const {isVisible: isLeadingEdgeVisible} = useIsVisible({ref: leadingEdgeRef})

  // Relative positioning within the visible roadmap viewport.  Add 1 to avoid fractional floating point errors.
  const viewport = getViewport()
  const isBefore = !isVisible && pillOffsetLeft + pillWidth <= viewport.left + 1
  const isAfter = !isVisible && pillOffsetLeft >= viewport.right - 1

  const timeSpanText = getTooltipText({start: startDate, end: endDate})

  const visiblePill = (
    <Label
      style={{
        width: pillWidth,
      }}
      /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
      className={clsx('roadmap-pill', styles.Label)}
      {...testIdProps('roadmap-view-group-header-item')}
    />
  )

  const pillContent = <div className={styles.Box}>{timeSpanText}</div>

  return (
    <RoadmapCell
      role="gridcell"
      data-date-start={startDate ? formatISODateString(startDate) : undefined}
      data-date-end={endDate ? formatISODateString(endDate) : undefined}
      className={styles.RoadmapCell}
    >
      <div ref={leadingEdgeRef} role={'presentation'} style={{left: pillOffsetLeft}} className={styles.leadingEdge} />
      <div ref={ref} style={{left: pillOffsetLeft}} className={styles.visiblePill}>
        {visiblePill}
      </div>
      {isVisible ? (
        <div
          style={{
            left: isLeadingEdgeVisible ? pillOffsetLeft + PILL_PADDING * 2 : tableWidth + PILL_STICKY_MARGIN,
          }}
          className={clsx(styles.pillContent, isLeadingEdgeVisible && styles.pillContentWithLeadingEdge)}
        >
          {pillContent}
        </div>
      ) : isBefore || isAfter ? (
        <NavigationArrow isBefore={isBefore} startDate={startDate} endDate={endDate} />
      ) : null}
    </RoadmapCell>
  )
})
